import {
  BUTTON_ACTION_TYPE,
  ButtonAction,
  ButtonComponent,
  CONSULTATION_LANDING_STEP,
  CONSULTATION_STEPS,
  Module,
  MODULE_TYPE,
  Step,
  STEP_TYPE
} from '@abiglobalhealth/go-types'

import { getPreviousModule } from '@/api/modules'
import { useCheckBoxModal } from '@/hooks/useCheckBoxModal'
import { useChoices } from '@/hooks/useChoices'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useIndications } from '@/hooks/useIndications'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { usePrescriptionFulfillmentOptions } from '@/hooks/usePrescriptionFulfillmentOptions'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import { HIDABLE_COMPONENTS, skipComponent } from '@/utils/isFieldFilled'
import { getInitialStep } from '@/utils/step'
import { formatTrace } from '@/utils/traces'

interface UseStepProps {
  navigateToStep: (stepId: string, updatedModule?: Module) => ButtonAction<BUTTON_ACTION_TYPE> | void
  navigateBack: () => Promise<void>
  navigateToModule: (module: Module) => ButtonAction<BUTTON_ACTION_TYPE> | void
  returnToStep: (stepId: string) => void
}

export const useNavigate = (): UseStepProps => {
  const { user } = useUser()
  const { initialFulfillmentOption } = usePrescriptionFulfillmentOptions()
  const { userUpdatedFields } = useUserForm()
  const { currentStep, setCurrentStep, updateFulfillmentStep } = useStep()
  const { currentModule, updateModule } = useModule()
  const { traces, setTraces, addTrace } = useTraces()
  const { opened: checkBoxModalOpened, close: closeCheckBoxModal } = useCheckBoxModal()
  const { removeChoiceByModuleAndStep } = useChoices()
  const { removeUntilStep, clearIndications } = useIndications()
  const { currentConsultationStep, updateStep } = useConsultationStep()
  const { clearFormErrors } = useFormErrors()
  const { showLoading, hideLoading } = useLoading()

  const shouldSkipStep = (step: Step) => {
    if (!step || !('hideIfFilled' in step) || !step.hideIfFilled) return false

    const skippedComponents = Object.values(step.components).some((component) => {
      if (!HIDABLE_COMPONENTS.includes(component.componentType)) return false

      return !skipComponent({
        component,
        user: user!,
        userUpdatedFields
      })
    })

    return !skippedComponents
  }

  const navigateToStep = (stepId: string, updatedModule?: Module) => {
    const module = updatedModule || currentModule
    if (!module) throw new Error('No current module found')
    if (!('steps' in module && module.steps)) throw new Error('No steps found in current module')

    const step = module.steps[stepId]

    if (!step) throw new Error('Next step not found in current module')

    const skipStep = shouldSkipStep(step)

    if (skipStep) return (step.components.button as ButtonComponent).buttonAction

    setCurrentStep(step)
    addTrace(module.id, step?.id)
  }

  const returnToStep = (stepId: string) => {
    if (!currentModule) throw new Error('No current module found')
    if (!currentStep) throw new Error('No current step found')
    if (!('steps' in currentModule && currentModule.steps)) throw new Error('No steps found in current module')

    let step = currentModule.steps[stepId]

    if (stepId === CONSULTATION_LANDING_STEP.id) {
      step = CONSULTATION_LANDING_STEP
      updateStep(CONSULTATION_STEPS.LANDING)
      setCurrentStep(CONSULTATION_LANDING_STEP)
    }

    if (!step) throw new Error('Step not found in current module')

    switch (step.type) {
      case STEP_TYPE.CHOICES:
        removeChoiceByModuleAndStep(currentModule.id, currentStep.id)
        break

      case STEP_TYPE.INDICATION_SURVEY:
        removeUntilStep(stepId)
        break
    }

    setCurrentStep(step)

    const stepTraceId = traces.findIndex((trace) => 'stepId' in trace && trace.stepId === stepId)
    setTraces((prev) => prev.slice(0, stepTraceId + 1))
    hideLoading()
  }

  const navigateToModule = (module: Module) => {
    if ('steps' in module && 'initialSteps' in module && module.initialSteps.length) {
      const initialStep = getInitialStep({ module, user: user! })
      if (!initialStep) throw new Error('No initial step found')

      const skipStep = shouldSkipStep(initialStep)

      if (skipStep) {
        updateModule(module)
        setTraces((prev) => [...prev, formatTrace(module.id)])
        return (initialStep.components?.button as ButtonComponent).buttonAction
      }

      setTraces((prev) => [...prev, formatTrace(module.id, initialStep.id, prev)])
      setCurrentStep(initialStep)
    }

    updateModule(module)

    addTrace(module.id)
  }

  const navigateBack = async () => {
    if (checkBoxModalOpened) return closeCheckBoxModal()

    if (currentConsultationStep === CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER) return updateStep(CONSULTATION_STEPS.LANDING)

    if (currentStep?.type === STEP_TYPE.FULFILLMENT_CHOICE_SELECTION) {
      updateFulfillmentStep({
        option: initialFulfillmentOption
      })
      return
    }

    showLoading()
    clearFormErrors()

    const currentTrace = traces[traces.length - 1]
    const previousTrace = traces[traces.length - 2]

    // Navigate to previous step if not first of module
    if (previousTrace && 'stepId' in previousTrace && currentTrace && 'isFirstOfModule' in currentTrace && !currentTrace.isFirstOfModule) {
      return returnToStep(previousTrace.stepId)
    }

    if (!user?.data?.uniqueId) throw new Error('No user uniqueId found')

    if (currentModule?.type === MODULE_TYPE.INDICATION_SURVEY) {
      clearIndications()
    }

    const { module } = await getPreviousModule({
      uniqueId: user?.data?.uniqueId
    })

    if (!module) throw new Error('No module found')

    setTraces((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev))

    if ('steps' in module && module.steps) {
      const previousModuleLastTrace = traces.filter((trace) => trace.moduleId === module.id).slice(-1)[0]

      const previousStep =
        previousModuleLastTrace && 'stepId' in previousModuleLastTrace
          ? module.steps[previousModuleLastTrace.stepId as string]
          : getInitialStep({ module, user })

      const skipStep = shouldSkipStep(previousStep)

      if (skipStep) {
        await navigateBack()
        return
      }

      setCurrentStep(previousStep)
    }

    updateModule(module)
    hideLoading()
  }

  return {
    navigateToStep,
    navigateBack,
    navigateToModule,
    returnToStep
  }
}
